/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  makeStyles
} from '@material-ui/core';
import {
  PieChart as PieChartIcon,
  Users as UsersIcon
} from 'react-feather';
import Logo from 'src/components/Logo';
import NavItem from './NavItem';
import usePermisos from 'src/hooks/usePermisos';
import Permisos from 'src/constantes/permisos';

const sections = [
  {
    items: [
      {
        title: 'Operación',
        icon: PieChartIcon,
        href: '/app/operacion',
        items: [
          {
            title: 'Prepedido',
            href: '/app/operacion/prepedido',
            permiso: Permisos.PREPEDIDO_VER,
          },
          {
            title: 'Programación asesor quirurgíco',
            href: '/app/operacion/asignacionsoportequirurgico',
            permiso: Permisos.ASIGNACIONSOPORTEQUIRURGICO_VER,
          },
          {
            title: 'Tarjeta de implante',
            href: '/app/operacion/reportegastos',
            permiso: Permisos.REPORTEGASTO_VER,
          },
        ],
      },
    ]
  },
  {
    items: [
      {
        title: 'Configuración',
        icon: UsersIcon,
        href: '/app/configuracion',
        items: [
          {
            title: 'EPS',
            href: '/app/configuracion/eps',
            permiso: Permisos.EPS_VER,
          },
          {
            title: 'Especialidades',
            href: '/app/configuracion/especialidades',
            permiso: Permisos.ESPECIALIDAD_VER,
          },
          {
            title: 'Universidad',
            href: '/app/configuracion/universidad',
            permiso: Permisos.UNIVERSIDAD_VER,
          },
          {
            title: 'Fellow',
            href: '/app/configuracion/fellow',
            permiso: Permisos.FELLOW_VER,
          },
          {
            title: 'Sub Especialidad',
            href: '/app/configuracion/subespecialidad',
            permiso: Permisos.SUBESPECIALIDAD_VER,
          },
          {
            title: 'Tipo afiliación',
            href: '/app/configuracion/tipoafiliacion',
            permiso: Permisos.TIPOAFILIACION_VER,
          },
          {
            title: 'Diagnóstico',
            href: '/app/configuracion/diagnostico',
            permiso: Permisos.DIAGNOSTICO_VER,
          },
          {
            title: 'Instituciones',
            href: '/app/configuracion/institucion',
            permiso: Permisos.INSTITUCION_VER,
          },
          {
            title: 'Pacientes',
            href: '/app/configuracion/pacientes',
            permiso: Permisos.PACIENTE_VER,
          },
          {
            title: 'Cirujanos',
            href: '/app/configuracion/cirujanos',
            permiso: Permisos.CIRUJANO_VER,
          },
          {
            title: 'Seguridad',
            href: '/app/configuracion/seguridad',
            permiso: Permisos.ROL_VER,
            items: [
              {
                title: 'Roles',
                href: '/app/configuracion/seguridad/roles',
                permiso: Permisos.ROL_VER,
              },
              {
                title: 'Usuarios',
                href: '/app/configuracion/seguridad/usuarios',
                permiso: Permisos.ROL_VER,
              },
            ],
          },
        ]
      },
    ],
  }
];

function filtrarPorPermiso(items, puede) {
  return items
    .filter(item => !item.permiso || puede(item.permiso))
    .map(item => ({
      ...item,
      items: item.items ? filtrarPorPermiso(item.items, puede) : undefined,
    }));
}

function renderNavItems({
  items,
  pathname,
  depth = 0
}) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  pathname,
  item,
  depth
}) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar = ({ onMobileClose, openMobile, user: { name = 'Genefix' } = {} }) => {
  const classes = useStyles();
  const location = useLocation();
  const { puede } = usePermisos();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            p={2}
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box
            display="flex"
            justifyContent="center"
          >
            <RouterLink to="/test">
              <Avatar
                alt="User"
                className={classes.avatar}
                src="../../../../public/static/images/avatars/avatar_1.png"
              />
            </RouterLink>
          </Box>
          <Box
            mt={2}
            textAlign="center"
          >
            <Link
              component={RouterLink}
              to="/test"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {name}
            </Link>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {sections.map((section) => (
            <List
              key={section.subheader}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {section.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: filtrarPorPermiso(section.items, puede),
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
